import type {
  TAnalysisPropertyDto,
  TRecommendationMode
} from '@homepal/api-contract'

import type { Lang, TFunction } from 'language/translator'

/** "1,2 mkr" from a million, otherwise to the nearest thousand: "450 000 kr". */
export const formatAmount = (value: number, lang: Lang): string => {
  const locale = lang === 'sv' ? 'sv-SE' : 'en-GB'

  if (value >= 1_000_000) {
    const millions = (value / 1_000_000).toLocaleString(locale, {
      maximumFractionDigits: 1
    })

    return lang === 'sv' ? `${millions} mkr` : `SEK ${millions}m`
  }

  const rounded = (Math.round(value / 1000) * 1000).toLocaleString(locale)

  return lang === 'sv' ? `${rounded} kr` : `SEK ${rounded}`
}

/**
 * Why the recommended properties were picked, by the mode stored at generation,
 * and what was left out below the noise.
 */
export const getRecommendationArgument = (
  qualified: TAnalysisPropertyDto[],
  mode: TRecommendationMode | null,
  t: TFunction,
  lang: Lang
): string | null => {
  const argument = getModeArgument(qualified, mode, t, lang)

  if (!argument) return null

  const belowNoise = qualified.filter(
    (item) => item.classification === 'not_prioritized'
  )

  if (belowNoise.length === 0) return argument

  const total = formatAmount(
    belowNoise.reduce((sum, item) => sum + (item.noi_change ?? 0), 0),
    lang
  )

  return `${argument} ${t(
    'analyses',
    belowNoise.length === 1
      ? 'recommendation_noise_excluded_one'
      : 'recommendation_noise_excluded',
    false,
    { count: String(belowNoise.length), total }
  )}`
}

/** The argument for the mode alone. */
const getModeArgument = (
  qualified: TAnalysisPropertyDto[],
  mode: TRecommendationMode | null,
  t: TFunction,
  lang: Lang
): string | null => {
  const potential = (item: TAnalysisPropertyDto) => item.noi_change ?? 0
  const sum = (items: TAnalysisPropertyDto[]) =>
    items.reduce((total, item) => total + potential(item), 0)
  const amount = (value: number) => formatAmount(value, lang)

  // Below the noise a property is never ranked; the caller notes them.
  const ranked = qualified
    .filter(
      (item) => item.classification !== 'not_prioritized' && potential(item) > 0
    )
    .sort((a, b) => potential(b) - potential(a))
  const recommended = ranked.filter(
    (item) => item.classification === 'recommended'
  )
  const rest = ranked.filter((item) => item.classification !== 'recommended')

  if (!mode || ranked.length === 0) return null

  if (mode === 'flat') {
    return t('analyses', 'recommendation_flat', false, {
      count: String(ranked.length),
      max: amount(potential(ranked[0])),
      min: amount(potential(ranked[ranked.length - 1]))
    })
  }

  if (mode === 'single') {
    return t('analyses', 'recommendation_single', false, {
      name: recommended[0].name,
      total: amount(potential(recommended[0]))
    })
  }

  const params = {
    count: String(recommended.length),
    last: amount(potential(recommended[recommended.length - 1])),
    name: recommended[0].name,
    next: rest.length > 0 ? amount(potential(rest[0])) : '',
    rest: String(rest.length),
    restTotal: amount(sum(rest)),
    top: amount(potential(ranked[0])),
    total: amount(sum(recommended))
  }

  const one = recommended.length === 1

  return t(
    'analyses',
    mode === 'gap'
      ? one
        ? 'recommendation_gap_one'
        : 'recommendation_gap'
      : one
        ? 'recommendation_anchor_one'
        : 'recommendation_anchor',
    false,
    params
  )
}
